import { ENTER } from '@angular/cdk/keycodes'
import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips'
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { TranslatePipe } from '@ngx-translate/core'
import { MAX_USAGE_ALERT_THRESHOLD_COUNT, MAX_USAGE_ALERT_THRESHOLD_PERCENTAGE, MIN_USAGE_ALERT_THRESHOLD_PERCENTAGE } from '../../metrics.service'

/**
 * Whether a typed threshold may join the list, so that the support form and the client's own panel
 * accept exactly the same values as the API does.
 */
export function canAddThreshold(value: number | null, existing: number[]): boolean {
  return (
    value !== null &&
    Number.isInteger(value) &&
    value >= MIN_USAGE_ALERT_THRESHOLD_PERCENTAGE &&
    value <= MAX_USAGE_ALERT_THRESHOLD_PERCENTAGE &&
    !existing.includes(value) &&
    existing.length < MAX_USAGE_ALERT_THRESHOLD_COUNT
  )
}

/** Parses what was typed into a chip; null for anything that is not a plain whole number. */
export function parseThreshold(raw: string): number | null {
  const trimmed = raw.trim().replace(/%$/, '')
  if (!/^\d+$/.test(trimmed)) {
    return null
  }
  return Number(trimmed)
}

/**
 * The percentages of a subscription allowance at which a client's administrators are emailed.
 *
 * Shared by the support client form, which submits them with the rest of the client's settings, and
 * the client's own usage panel, which saves on every change — so the two cannot drift apart on what
 * they accept or how they order it.
 */
@Component({
  selector: 'app-threshold-chips-input',
  standalone: true,
  imports: [MatChipGrid, MatChipRow, MatChipInput, MatChipRemove, MatFormField, MatHint, MatLabel, MatIcon, TranslatePipe],
  templateUrl: './threshold-chips-input.component.html',
  styleUrl: './threshold-chips-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: ThresholdChipsInputComponent, multi: true }],
})
export class ThresholdChipsInputComponent implements ControlValueAccessor {
  @Input()
  labelKey = 'METRICS.SUBSCRIPTION_USAGE.ALERTS_LABEL'

  @Input()
  hintKey: string | null = null

  thresholds: number[] = []
  disabled = false
  readonly separatorKeysCodes = [ENTER] as const
  readonly maxCount = MAX_USAGE_ALERT_THRESHOLD_COUNT

  private onChange: (value: number[]) => void = () => {}
  private onTouched: () => void = () => {}

  writeValue(value: number[] | null | undefined) {
    this.thresholds = Array.isArray(value) ? [...value].sort((a, b) => a - b) : []
  }

  registerOnChange(fn: (value: number[]) => void) {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled
  }

  add(event: MatChipInputEvent) {
    const threshold = parseThreshold(event.value ?? '')
    if (canAddThreshold(threshold, this.thresholds)) {
      this.thresholds = [...this.thresholds, threshold!].sort((a, b) => a - b)
      this.onChange(this.thresholds)
    }
    // Cleared either way: leaving a rejected value in the field reads as though it were pending.
    event.chipInput?.clear()
    this.onTouched()
  }

  remove(threshold: number) {
    this.thresholds = this.thresholds.filter((existing) => existing !== threshold)
    this.onChange(this.thresholds)
    this.onTouched()
  }
}
